"use client";

/**
 * P3-08 · Buy-box purchasing actions.
 *
 * Orchestrates the quantity stepper + add-to-cart + (flag-gated) buy-now + the
 * mobile sticky bar against the variant engine. Owns the quantity state and
 * derives the shared disabled/hint state from the resolved variant, keeping the
 * inline CTA and the sticky bar in lockstep.
 */
import * as React from "react";

import { useFeatureFlag } from "~/components/feature-flags/provider";
import { useVariant } from "~/components/product/variant";
import { CART_BACKORDER_CAP } from "~/config/storefront";
import {
  clampQuantity,
  type CartPersonalisation,
} from "~/lib/cart/cart-interface";

import { deriveActionState } from "./action-state";
import { AddToCartButton } from "./AddToCartButton";
import { BackInStockControl } from "./BackInStockControl";
import { BuyNowButton } from "./BuyNowButton";
import { QuantityStepper } from "./QuantityStepper";
import { SecondaryActions } from "./SecondaryActions";
import { StickyBuyBar } from "./StickyBuyBar";

export function BuyBoxActions({
  productId,
  title,
  signedIn,
  className,
  personalisation = null,
  personalisationValid = true,
  onQuantityChange,
}: {
  productId: string;
  title: string;
  /** Resolved server-side (no SessionProvider on the storefront) — gates
   *  server vs local wishlist persistence + the guest→account merge (P3-09). */
  signedIn: boolean;
  className?: string;
  /**
   * P3-07 personalisation payload, carried opaquely into add-to-cart. Null when
   * the product has no custom options (or the flag is off).
   */
  personalisation?: CartPersonalisation;
  /**
   * P3-07: false when required custom fields are incomplete — gates the CTA
   * alongside the variant/stock state.
   */
  personalisationValid?: boolean;
  /** Mirrors the clamped buy quantity for presentation-only estimates. */
  onQuantityChange?: (quantity: number) => void;
}) {
  const { resolvedVariant, isComplete, validationMessage } = useVariant();
  const buyNowEnabled = useFeatureFlag("pdpBuyNow");
  const backInStockEnabled = useFeatureFlag("backInStock");
  const stickyAtcEnabled = useFeatureFlag("mobileStickyAtc");
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const variantState = deriveActionState({
    resolvedVariant,
    isComplete,
    validationMessage,
    backorderCap: CART_BACKORDER_CAP,
  });
  const { reason, max } = variantState;
  // Personalisation gates the CTA on top of the variant/stock state.
  const disabled = variantState.disabled || !personalisationValid;

  const [quantity, setQuantity] = React.useState(1);
  // Keep the quantity within the current variant's bound — switching to a
  // lower-stock variant clamps the field down rather than over-ordering.
  React.useEffect(() => {
    if (max >= 1) setQuantity((q) => clampQuantity(q, max));
  }, [max]);

  const variantId = resolvedVariant?.id ?? "";
  const qty = max >= 1 ? quantity : 1;

  React.useEffect(() => {
    onQuantityChange?.(qty);
  }, [onQuantityChange, qty]);

  return (
    <div className={className} data-testid="pdp-actions">
      {max >= 1 && (
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm font-medium text-fg">Quantity</span>
          <QuantityStepper value={qty} max={max} onChange={setQuantity} />
        </div>
      )}

      {/* Sentinel: the sticky bar shows once this inline CTA scrolls off. */}
      <div ref={sentinelRef} className="mt-4 space-y-2">
        <AddToCartButton
          variantId={variantId}
          quantity={qty}
          personalisation={personalisation}
          disabled={disabled}
          reason={reason}
        />
        {buyNowEnabled && (
          <BuyNowButton
            variantId={variantId}
            quantity={qty}
            personalisation={personalisation}
            disabled={disabled}
          />
        )}
      </div>

      {/* P5-15 · Out-of-stock but a specific variant is fully chosen → let the
          shopper subscribe to a restock alert. */}
      {backInStockEnabled && resolvedVariant && isComplete && max < 1 ? (
        <BackInStockControl
          variantId={resolvedVariant.id}
          signedIn={signedIn}
        />
      ) : null}

      {/* P3-09 · wishlist / compare / save-for-later (each flag-gated). */}
      <SecondaryActions
        productId={productId}
        signedIn={signedIn}
        className="mt-3"
      />

      {stickyAtcEnabled ? (
        <StickyBuyBar
          sentinelRef={sentinelRef}
          title={title}
          variantId={variantId}
          quantity={qty}
          personalisation={personalisation}
          disabled={disabled}
          reason={reason}
          priceMinor={resolvedVariant?.priceMinor ?? null}
          currency={resolvedVariant?.currency ?? "GBP"}
        />
      ) : null}
    </div>
  );
}
